/**
 * Event Permissions Hooks
 * React Query hooks for managing event permissions
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UUID } from "../types/common.types";
import type { PermissionLevel, UserEventAccess } from "../types/event.types";
import {
  getEventPermissions,
  getUserEventAccess,
  addEventPermission,
  updateEventPermission,
  removeEventPermission,
  getEventOwnerCount,
  searchUsersForSharing,
} from "../services/eventPermissions.service";

// Query keys
export const eventPermissionsKeys = {
  all: ["eventPermissions"] as const,
  list: (eventId: UUID) =>
    [...eventPermissionsKeys.all, "list", eventId] as const,
  access: (eventId: UUID) =>
    [...eventPermissionsKeys.all, "access", eventId] as const,
  ownerCount: (eventId: UUID) =>
    [...eventPermissionsKeys.all, "ownerCount", eventId] as const,
  searchUsers: (eventId: UUID, query: string) =>
    [...eventPermissionsKeys.all, "search", eventId, query] as const,
};

/**
 * Hook to get all permissions for an event
 */
export function useEventPermissions(eventId: UUID | undefined) {
  return useQuery({
    queryKey: eventPermissionsKeys.list(eventId!),
    queryFn: () => getEventPermissions(eventId!),
    enabled: !!eventId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to get current user's access for an event
 */
export function useUserEventAccess(eventId: UUID | undefined) {
  return useQuery<UserEventAccess | null>({
    queryKey: eventPermissionsKeys.access(eventId!),
    queryFn: () => getUserEventAccess(eventId!),
    enabled: !!eventId,
    staleTime: 30000,
  });
}

/**
 * Hook to get owner count for an event
 */
export function useEventOwnerCount(eventId: UUID | undefined) {
  return useQuery({
    queryKey: eventPermissionsKeys.ownerCount(eventId!),
    queryFn: () => getEventOwnerCount(eventId!),
    enabled: !!eventId,
  });
}

/**
 * Hook to search users for sharing
 */
export function useSearchUsersForSharing(
  eventId: UUID | undefined,
  searchQuery: string,
) {
  return useQuery({
    queryKey: eventPermissionsKeys.searchUsers(eventId!, searchQuery),
    queryFn: () => searchUsersForSharing(eventId!, searchQuery),
    enabled: !!eventId && searchQuery.length >= 2,
    staleTime: 10000, // 10 seconds
  });
}

/**
 * Hook to add a permission
 */
export function useAddEventPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      userId,
      permissionLevel,
    }: {
      eventId: UUID;
      userId: UUID;
      permissionLevel: PermissionLevel;
    }) =>
      addEventPermission({
        event_id: eventId,
        user_id: userId,
        permission_level: permissionLevel,
      }),
    onSuccess: (data) => {
      // Invalidate permissions list
      queryClient.invalidateQueries({
        queryKey: eventPermissionsKeys.list(data.event_id),
      });
      // Invalidate owner count
      queryClient.invalidateQueries({
        queryKey: eventPermissionsKeys.ownerCount(data.event_id),
      });
    },
  });
}

/**
 * Hook to update a permission
 */
export function useUpdateEventPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      userId,
      permissionLevel,
    }: {
      eventId: UUID;
      userId: UUID;
      permissionLevel: PermissionLevel;
    }) => updateEventPermission(eventId, userId, permissionLevel),
    onSuccess: (data) => {
      // Invalidate permissions list
      queryClient.invalidateQueries({
        queryKey: eventPermissionsKeys.list(data.event_id),
      });
      // Invalidate owner count
      queryClient.invalidateQueries({
        queryKey: eventPermissionsKeys.ownerCount(data.event_id),
      });
      // Invalidate user's access in case it's the current user
      queryClient.invalidateQueries({
        queryKey: eventPermissionsKeys.access(data.event_id),
      });
    },
  });
}

/**
 * Hook to remove a permission
 */
export function useRemoveEventPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, userId }: { eventId: UUID; userId: UUID }) =>
      removeEventPermission(eventId, userId),
    onSuccess: (_, variables) => {
      // Invalidate permissions list
      queryClient.invalidateQueries({
        queryKey: eventPermissionsKeys.list(variables.eventId),
      });
      // Invalidate owner count
      queryClient.invalidateQueries({
        queryKey: eventPermissionsKeys.ownerCount(variables.eventId),
      });
    },
  });
}

/**
 * Utility hook to check if current user can edit
 */
export function useCanEditEvent(eventId: UUID | undefined) {
  const { data: access, isLoading } = useUserEventAccess(eventId);
  return {
    canEdit: access?.can_edit ?? false,
    canManage: access?.can_manage ?? false,
    isOwner: access?.is_owner ?? false,
    isLoading,
    access,
  };
}
